import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './MonthlyReport.module.css';
import apiClient from '../../../api/client';
import { stages } from '../../../data/studentsData';
import { MONTHLY_SUBJECTS, subjectTotalScore, MONTHLY_TOTAL_SCORE } from '../../../data/monthlyExamPlan';
import { getCairoToday } from '../../../utils/cairoDate';

// صفحة جديدة بالكامل - مش بتلمس ولا حرف في أي صفحة أو راوت موجود.
// بتجمع كل امتحانات الشهر (أونلاين + ورقي) لكل مادة في تقرير واحد لكل طالب،
// وبتدي اختيار: تبعت درجة كل مادة لوحدها لولي الأمر، أو تبعت تقرير الشهر كامل.

const currentCairoMonth = () => getCairoToday().slice(0, 7); // "YYYY-MM"

const MonthlyReport = () => {
  const navigate = useNavigate();
  const [allExams, setAllExams] = useState([]);
  const [allGrades, setAllGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(currentCairoMonth());

  useEffect(() => {
    const loadData = async () => {
      try {
        const [examsRes, gradesRes] = await Promise.all([
          apiClient.get('/exams'),       // الراوت الموجود أصلاً - من غير أي تعديل
          apiClient.get('/grades'),      // نفس الراوت الموجود - من غير فلتر بيرجع كل الدرجات
        ]);
        setAllExams(examsRes.data);
        setAllGrades(gradesRes.data);
      } catch (error) {
        console.error('❌ مشكلة في جلب بيانات تقرير الشهر:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // خريطة سريعة لكل امتحان بالـ id عشان نجيب المادة/النوع/التاريخ منه
  const examsMap = useMemo(() => {
    const map = {};
    allExams.forEach((exam) => { map[exam._id] = exam; });
    return map;
  }, [allExams]);

  // الامتحانات اللي بتخص المرحلة والشهر المختارين بس
  const relevantExamIds = useMemo(() => {
    const set = new Set();
    allExams.forEach((exam) => {
      if (selectedStage && exam.stage !== selectedStage) return;
      const examMonth = exam.date ? String(exam.date).slice(0, 7) : '';
      if (selectedMonth && examMonth !== selectedMonth) return;
      set.add(exam._id);
    });
    return set;
  }, [allExams, selectedStage, selectedMonth]);

  // تجميع الدرجات لكل طالب -> لكل مادة -> { online, paper }
  const studentsReport = useMemo(() => {
    const byStudent = {};

    allGrades.forEach((grade) => {
      if (!grade.student || !grade.exam) return;
      const examFull = examsMap[grade.exam._id];
      if (!examFull || !relevantExamIds.has(examFull._id)) return;

      const subjectMeta = MONTHLY_SUBJECTS.find((s) => s.label === examFull.subject);
      if (!subjectMeta) return; // مش من المواد الشهرية الثابتة، تجاهله

      const isPaper = (examFull.type || '').includes('ورقي');

      const sid = grade.student._id;
      if (!byStudent[sid]) {
        byStudent[sid] = { student: grade.student, subjects: {} };
      }
      if (!byStudent[sid].subjects[subjectMeta.key]) {
        byStudent[sid].subjects[subjectMeta.key] = {
          label: subjectMeta.label,
          online: 0,
          onlineCount: 0,
          paper: null,
          maxTotal: subjectTotalScore(subjectMeta.key),
        };
      }

      const bucket = byStudent[sid].subjects[subjectMeta.key];
      if (isPaper) {
        bucket.paper = grade.score;
      } else {
        bucket.online += Number(grade.score) || 0;
        bucket.onlineCount += 1;
      }
    });

    return Object.values(byStudent).map((row) => {
      let grandTotal = 0;
      Object.values(row.subjects).forEach((s) => {
        grandTotal += (s.online || 0) + (s.paper || 0);
      });
      return { ...row, grandTotal };
    });
  }, [allGrades, examsMap, relevantExamIds]);

  const buildSubjectLine = (subjectData) => {
    const total = (subjectData.online || 0) + (subjectData.paper || 0);
    return `• ${subjectData.label}: ${total} من ${subjectData.maxTotal}`;
  };

  // إرسال درجة مادة واحدة بس لولي الأمر
  const sendSingleSubject = (row, subjectKey) => {
    const subjectData = row.subjects[subjectKey];
    if (!subjectData) return;

    const message =
`📚 سلسلة الأزهري في العلوم الشرعية
من معلمي المواد الشرعية:
أ/ حسام صبري الأزهري
أ/ محمد كامل الشيمي
إلى السيد/ ولي أمر الطالب: ${row.student?.name || ''}
السلام عليكم ورحمة الله وبركاته.
يسرنا أن نحيط سيادتكم علمًا بنتيجة نجلكم/ابنتكم في امتحان مادة (${subjectData.label}) لهذا الشهر:
${buildSubjectLine(subjectData)}
ونسأل الله أن يوفقه إلى كل خير.
مع خالص الشكر والتقدير،
سلسلة الأزهري في العلوم الشرعية`;

    const phone = (row.student?.parentPhone || '').replace(/^0/, '20');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // إرسال تقرير الشهر كامل بكل المواد مرة واحدة
  const sendFullMonth = (row) => {
    const lines = Object.values(row.subjects).map(buildSubjectLine);

    const message =
`📚 سلسلة الأزهري في العلوم الشرعية
من معلمي المواد الشرعية:
أ/ حسام صبري الأزهري
أ/ محمد كامل الشيمي
إلى السيد/ ولي أمر الطالب: ${row.student?.name || ''}
السلام عليكم ورحمة الله وبركاته.
يسرنا أن نحيط سيادتكم علمًا بنتيجة نجلكم/ابنتكم في امتحانات هذا الشهر:
${lines.join('\n')}
─────────────
📊 المجموع الكلي: ${row.grandTotal} من ${MONTHLY_TOTAL_SCORE}
ونسأل الله أن يوفقه إلى كل خير، ونؤكد أن هذه المتابعة تأتي في إطار حرصنا على التواصل المستمر مع أولياء الأمور.
مع خالص الشكر والتقدير،
سلسلة الأزهري في العلوم الشرعية`;

    const phone = (row.student?.parentPhone || '').replace(/^0/, '20');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) return <div style={{ padding: '2rem' }}>جاري تحميل تقرير الشهر...</div>;

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.header}>
        <div className={styles.titleBox}>
          <h1 className={styles.title}>تقرير الشهر الكامل</h1>
          <span className={styles.subtitle}>
            كل درجات الأونلاين والورقي لكل مادة مجمعة لكل طالب
          </span>
        </div>
        <button className={styles.backBtn} onClick={() => navigate('/exams')}>
          رجوع للامتحانات
        </button>
      </div>

      <div className={styles.filtersContainer}>
        <select
          className={styles.selectBox}
          value={selectedStage}
          onChange={(e) => setSelectedStage(e.target.value)}
        >
          <option value="">كل المراحل الدراسية</option>
          {stages.map((stage, index) => (
            <option key={index} value={stage}>{stage}</option>
          ))}
        </select>

        <input
          type="month"
          className={styles.selectBox}
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        />
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>كود الطالب</th>
              <th>الاسم</th>
              {MONTHLY_SUBJECTS.map((s) => (
                <th key={s.key}>{s.label}</th>
              ))}
              <th>المجموع الكلي</th>
              <th>إرسال لولي الأمر</th>
            </tr>
          </thead>
          <tbody>
            {studentsReport.length > 0 ? studentsReport.map((row) => (
              <tr key={row.student._id}>
                <td>{row.student?.code}</td>
                <td>{row.student?.name}</td>
                {MONTHLY_SUBJECTS.map((s) => {
                  const subjectData = row.subjects[s.key];
                  if (!subjectData) return <td key={s.key}>—</td>;
                  const total = (subjectData.online || 0) + (subjectData.paper || 0);
                  return (
                    <td key={s.key}>
                      <div>{total} / {subjectData.maxTotal}</div>
                      <button
                        className={styles.smallWhatsappBtn}
                        onClick={() => sendSingleSubject(row, s.key)}
                      >
                        💬 إرسال المادة دي
                      </button>
                    </td>
                  );
                })}
                <td><strong>{row.grandTotal} / {MONTHLY_TOTAL_SCORE}</strong></td>
                <td>
                  <button className={styles.whatsappBtn} onClick={() => sendFullMonth(row)}>
                    💬 إرسال تقرير الشهر كامل
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={MONTHLY_SUBJECTS.length + 4} style={{ textAlign: 'center', color: '#6b7280' }}>
                  مفيش درجات مسجلة للمرحلة والشهر ده لسه
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MonthlyReport;
